import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Briefcase, Search, MapPin, Building2, Clock, Globe, ChevronUp, Sparkles } from "lucide-react";
import { usePreviewSafeData } from "../hooks/usePreviewSafeData";
import CategoryHero from "./CategoryHero";
import NewsTicker from "./NewsTicker";

const JOBS_BG =
  "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780931862393-l37b6x.png";

const JOB_CATEGORIES = [
  "Restaurant / Food Service",
  "Retail & Grocery",
  "IT & Tech",
  "Healthcare",
  "Beauty & Salon",
  "Event Services",
  "Accounting & Finance",
  "Sales & Marketing",
  "Customer Service",
  "Driving & Logistics",
  "Childcare & Education",
  "Other",
] as const;

const JOB_TYPES = ["Full-Time", "Part-Time", "Contract", "Internship"] as const;

const WORK_ARRANGEMENTS = ["On-site", "Hybrid", "Remote"] as const;

const SERVICE_CATEGORIES = [
  "Babysitting & Childcare",
  "Tutoring & Education",
  "Home & Personal Services",
  "Food & Catering",
  "Beauty & Wellness",
  "Photography & Events",
  "Professional & Business Services",
  "Tech & Computer Help",
  "Transportation",
  "Other",
] as const;

const initialServiceForm = {
  name: "",
  serviceOffered: "",
  serviceCategory: "",
  shortDescription: "",
  location: "",
  contactEmail: "",
  phone: "",
  website: "",
};

// Featured job listings published by the Dallas Desi Scoop team.
// These render above the community-submitted listings in the openings section.
const FEATURED_JOBS = [
  {
    id: "featured-gehi-law-telugu-rep",
    jobTitle: "Telugu-Speaking Representative",
    companyName: "Gehi Law Firm",
    jobType: "Full-Time",
    location: "Dallas, TX",
    workArrangement: "On-site",
    jobCategory: "Status (Filled)",
    companyWebsite: "https://gehilaw.com/",
    postedDate: "Sept 14, 2026",
    about:
      "A law firm in Dallas, Texas is looking for a Telugu-Speaking Representative to join their team. This is a great opportunity for candidates with strong community connections and communication skills. No prior legal experience is required.",
    whoCanApply: [
      "Excellent communication skills in English and Telugu",
      "Students with valid work permits / work authorization can apply",
      "Strong networking skills within the Telugu community",
      "Proficient typing skills",
      "Decent technology / computer skills",
      "Valid driver's license required",
    ],
    sponsorship:
      "Previous legal experience is not required. Legal training will be provided. The firm will also consider H-1B and Permanent Residence sponsorship for qualified candidates.",
    howToApply: "Email your resume to Dallasdesiscoop@gmail.com or subrato@gehilaw.com",
  },
];

const initialForm = {
  jobTitle: "",
  companyName: "",
  jobCategory: "",
  jobType: "",
  location: "",
  workArrangement: "",
  jobDescription: "",
  howToApply: "",
  companyWebsite: "",
};

// Rows may be flattened (id + fields) or nested (data: {...}) depending on
// the backend shape — read defensively everywhere.
function field(row: any, key: string): string {
  const v = row?.[key] ?? row?.data?.[key];
  return typeof v === "string" ? v : v ? String(v) : "";
}

export default function Jobs() {
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ ...initialForm });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [serviceFormOpen, setServiceFormOpen] = useState(false);
  const [serviceForm, setServiceForm] = useState({ ...initialServiceForm });
  const [serviceErrors, setServiceErrors] = useState<Record<string, string>>({});
  const [serviceSubmitted, setServiceSubmitted] = useState(false);
  const [serviceSubmitError, setServiceSubmitError] = useState<string | null>(null);

  const { insert, loading } = usePreviewSafeData("job_postings");
  const { insert: insertService, loading: serviceLoading } = usePreviewSafeData("service_offers");

  // Approved job listings for the "Looking for a Job" section
  const { rows: jobRows, loading: jobsLoading } = usePreviewSafeData("job_postings");
  const approvedJobs = (jobRows || []).filter((r: any) => (field(r, "status") || "pending") === "approved");
  const totalOpenings = FEATURED_JOBS.length + approvedJobs.length;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.jobTitle.trim()) errs.jobTitle = "Job Title is required";
    if (!form.companyName.trim()) errs.companyName = "Company / Business Name is required";
    if (!form.jobCategory.trim()) errs.jobCategory = "Job Category is required";
    if (!form.jobType.trim()) errs.jobType = "Job Type is required";
    if (!form.location.trim()) errs.location = "Location is required";
    if (!form.jobDescription.trim()) errs.jobDescription = "Job Description is required";
    if (!form.howToApply.trim()) errs.howToApply = "How to Apply is required";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    try {
      await insert({
        jobTitle: form.jobTitle.trim(),
        companyName: form.companyName.trim(),
        jobCategory: form.jobCategory,
        jobType: form.jobType,
        location: form.location.trim(),
        workArrangement: form.workArrangement,
        jobDescription: form.jobDescription.trim(),
        howToApply: form.howToApply.trim(),
        companyWebsite: form.companyWebsite.trim(),
        status: "pending",
        submittedAt: new Date().toISOString(),
      });
      setSubmitted(true);
      setForm({ ...initialForm });
      setErrors({});
    } catch (err) {
      setSubmitError(
        err instanceof Error && err.message
          ? "Your job posting could not be submitted. Please try again. (" + err.message + ")"
          : "Your job posting could not be submitted. Please try again."
      );
      console.error("Job posting submission failed:", err);
    }
  };

  const handleServiceChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setServiceForm((prev) => ({ ...prev, [name]: value }));
    if (serviceErrors[name]) {
      setServiceErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validateService = () => {
    const errs: Record<string, string> = {};
    if (!serviceForm.name.trim()) errs.name = "Name is required";
    if (!serviceForm.serviceOffered.trim()) errs.serviceOffered = "Service You Offer is required";
    if (!serviceForm.serviceCategory.trim()) errs.serviceCategory = "Service Category is required";
    if (!serviceForm.shortDescription.trim()) errs.shortDescription = "Short Description is required";
    if (!serviceForm.location.trim()) errs.location = "Location / Areas Served is required";
    if (!serviceForm.contactEmail.trim()) errs.contactEmail = "Contact Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(serviceForm.contactEmail.trim()))
      errs.contactEmail = "Please enter a valid email address";
    return errs;
  };

  const handleServiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateService();
    if (Object.keys(errs).length) {
      setServiceErrors(errs);
      return;
    }
    try {
      await insertService({
        name: serviceForm.name.trim(),
        serviceOffered: serviceForm.serviceOffered.trim(),
        serviceCategory: serviceForm.serviceCategory,
        shortDescription: serviceForm.shortDescription.trim(),
        location: serviceForm.location.trim(),
        contactEmail: serviceForm.contactEmail.trim(),
        phone: serviceForm.phone.trim(),
        website: serviceForm.website.trim(),
        status: "pending",
        submittedAt: new Date().toISOString(),
      });
      setServiceSubmitted(true);
      setServiceForm({ ...initialServiceForm });
      setServiceErrors({});
    } catch (err) {
      setServiceSubmitError(
        err instanceof Error && err.message
          ? "Your service listing could not be submitted. Please try again. (" + err.message + ")"
          : "Your service listing could not be submitted. Please try again."
      );
      console.error("Service listing submission failed:", err);
    }
  };

  const fieldClass =
    "w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-[#1f2937] bg-white focus:ring-2 focus:ring-[#51ba4f] focus:border-transparent outline-none transition-shadow";
  const labelClass = "block text-sm font-semibold text-[#1f2937] mb-1";
  const sectionClass = "border-b border-gray-200 pb-5 mb-5 last:border-0 last:pb-0 last:mb-0";

  const scrollToJobs = () => {
    document.getElementById("job-listings")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Jobs"
        placeholder="Search Jobs…"
        backgroundImage={JOBS_BG}
      />
      <NewsTicker />

      <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-left"
        >
          <h2
            className="text-lg md:text-xl font-bold text-[#1f2937]"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Dallas Desi Jobs — Hire Local, Work Local
          </h2>
          <p
            className="text-sm md:text-base text-gray-600 mt-4 max-w-2xl"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Discover job opportunities and hiring opportunities across Dallas–Fort Worth with
            Dallas Desi Scoop. Browse full-time, part-time, contract, and other employment
            opportunities in IT and technology, restaurants and hospitality, healthcare, retail,
            education, administration, and more. Looking for a job in Dallas, Frisco, Plano,
            Irving, McKinney, Carrollton, Richardson, or surrounding DFW communities? Explore
            available job postings, share your profile with employers, or post a job opening to
            connect with qualified candidates.
          </p>
          <p className="text-xs text-gray-400 mt-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Real businesses. Real openings. Real local opportunity.
          </p>
        </motion.div>

        {/* ── POST A JOB form (toggle) ── */}
        <AnimatePresence>
          {formOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="overflow-hidden mt-6"
            >
              <div className="max-w-2xl bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm">
                {submitted ? (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-center py-8"
                  >
                    <div className="text-4xl mb-3">🎉</div>
                    <h3 className="text-xl font-bold text-[#1f2937] mb-2">
                      Job Submitted for Review!
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Thanks for posting! Your job will appear in the listings below once our team
                      approves it.
                    </p>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setFormOpen(false);
                      }}
                      className="text-sm font-semibold text-[#51ba4f] hover:underline"
                    >
                      Close
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-0">
                    {/* Job Details */}
                    <div className={sectionClass}>
                      <h3
                        className="text-base font-bold text-[#1f2937] mb-4"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        Job Details
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>
                            Job Title <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="jobTitle"
                            value={form.jobTitle}
                            onChange={handleChange}
                            placeholder="Cashier, Tandoori Chef, Sales Associate..."
                            className={fieldClass}
                          />
                          {errors.jobTitle && (
                            <p className="text-red-500 text-xs mt-1">{errors.jobTitle}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            Company / Business Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="companyName"
                            value={form.companyName}
                            onChange={handleChange}
                            placeholder="Your business name"
                            className={fieldClass}
                          />
                          {errors.companyName && (
                            <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            Job Category <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="jobCategory"
                            value={form.jobCategory}
                            onChange={handleChange}
                            className={fieldClass}
                          >
                            <option value="">Select a category</option>
                            {JOB_CATEGORIES.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                          {errors.jobCategory && (
                            <p className="text-red-500 text-xs mt-1">{errors.jobCategory}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            Job Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="jobType"
                            value={form.jobType}
                            onChange={handleChange}
                            className={fieldClass}
                          >
                            <option value="">Select job type</option>
                            {JOB_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                          {errors.jobType && (
                            <p className="text-red-500 text-xs mt-1">{errors.jobType}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            Location (City / Area) <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="location"
                            value={form.location}
                            onChange={handleChange}
                            placeholder="Dallas, Frisco, Plano, Irving..."
                            className={fieldClass}
                          />
                          {errors.location && (
                            <p className="text-red-500 text-xs mt-1">{errors.location}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>Work Arrangement</label>
                          <select
                            name="workArrangement"
                            value={form.workArrangement}
                            onChange={handleChange}
                            className={fieldClass}
                          >
                            <option value="">Select arrangement</option>
                            {WORK_ARRANGEMENTS.map((w) => (
                              <option key={w} value={w}>
                                {w}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Description & Apply */}
                    <div className={sectionClass}>
                      <h3
                        className="text-base font-bold text-[#1f2937] mb-4"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        Description & Application
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className={labelClass}>
                            Job Description <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            name="jobDescription"
                            value={form.jobDescription}
                            onChange={handleChange}
                            rows={4}
                            placeholder="Short description of the role, responsibilities, and requirements..."
                            className={fieldClass}
                          />
                          {errors.jobDescription && (
                            <p className="text-red-500 text-xs mt-1">{errors.jobDescription}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            How to Apply <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            name="howToApply"
                            value={form.howToApply}
                            onChange={handleChange}
                            rows={3}
                            placeholder="Email your resume to..., call..., or apply in person at..."
                            className={fieldClass}
                          />
                          {errors.howToApply && (
                            <p className="text-red-500 text-xs mt-1">{errors.howToApply}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>Company Website (optional)</label>
                          <input
                            name="companyWebsite"
                            value={form.companyWebsite}
                            onChange={handleChange}
                            placeholder="https://yourbusiness.com"
                            className={fieldClass}
                          />
                        </div>
                      </div>
                    </div>

                    {submitError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-[12.5px] rounded-xl px-4 py-3 mb-3">
                        {submitError}
                      </div>
                    )}
                    {/* Submit */}
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={loading}
                        className="bg-[#51ba4f] hover:bg-[#43a041] text-white text-sm font-bold px-6 py-2.5 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {loading ? "Submitting…" : "Submit Job Posting"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormOpen(false)}
                        className="text-sm font-semibold text-gray-500 hover:text-gray-700 px-4 py-2.5 rounded-md transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── LIST YOUR SERVICE form (toggle) ── */}
        <AnimatePresence>
          {serviceFormOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="overflow-hidden mt-6"
            >
              <div className="max-w-2xl bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm">
                {serviceSubmitted ? (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-center py-8"
                  >
                    <div className="text-4xl mb-3">🎉</div>
                    <h3 className="text-xl font-bold text-[#1f2937] mb-2">
                      Service Submitted for Review!
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Thanks for sharing your service! Your listing will go live once our team
                      approves it.
                    </p>
                    <button
                      onClick={() => {
                        setServiceSubmitted(false);
                        setServiceFormOpen(false);
                      }}
                      className="text-sm font-semibold text-[#51ba4f] hover:underline"
                    >
                      Close
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleServiceSubmit} className="space-y-0">
                    {/* Service Details */}
                    <div className={sectionClass}>
                      <h3
                        className="text-base font-bold text-[#1f2937] mb-4"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        LIST YOUR SERVICE
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>
                            Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="name"
                            value={serviceForm.name}
                            onChange={handleServiceChange}
                            placeholder="Your full name"
                            className={fieldClass}
                          />
                          {serviceErrors.name && (
                            <p className="text-red-500 text-xs mt-1">{serviceErrors.name}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            Service You Offer <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="serviceOffered"
                            value={serviceForm.serviceOffered}
                            onChange={handleServiceChange}
                            placeholder="Babysitting, Tutoring, Home Cooking..."
                            className={fieldClass}
                          />
                          {serviceErrors.serviceOffered && (
                            <p className="text-red-500 text-xs mt-1">{serviceErrors.serviceOffered}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            Service Category <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="serviceCategory"
                            value={serviceForm.serviceCategory}
                            onChange={handleServiceChange}
                            className={fieldClass}
                          >
                            <option value="">Select a category</option>
                            {SERVICE_CATEGORIES.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                          {serviceErrors.serviceCategory && (
                            <p className="text-red-500 text-xs mt-1">{serviceErrors.serviceCategory}</p>
                          )}
                        </div>
                        <div>
                          <label className={labelClass}>
                            Location / Areas Served <span className="text-red-500">*</span>
                          </label>
                          <input
                            name="location"
                            value={serviceForm.location}
                            onChange={handleServiceChange}
                            placeholder="Dallas, Frisco, Plano, Irving..."
                            className={fieldClass}
                          />
                          {serviceErrors.location && (
                            <p className="text-red-500 text-xs mt-1">{serviceErrors.location}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Description & Contact */}
                    <div className={sectionClass}>
                      <h3
                        className="text-base font-bold text-[#1f2937] mb-4"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        Description & Contact
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className={labelClass}>
                            Short Description <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            name="shortDescription"
                            value={serviceForm.shortDescription}
                            onChange={handleServiceChange}
                            rows={4}
                            placeholder="Tell the community about your service, skills, availability..."
                            className={fieldClass}
                          />
                          {serviceErrors.shortDescription && (
                            <p className="text-red-500 text-xs mt-1">{serviceErrors.shortDescription}</p>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className={labelClass}>
                              Contact Email <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              name="contactEmail"
                              value={serviceForm.contactEmail}
                              onChange={handleServiceChange}
                              placeholder="you@example.com"
                              className={fieldClass}
                            />
                            {serviceErrors.contactEmail && (
                              <p className="text-red-500 text-xs mt-1">{serviceErrors.contactEmail}</p>
                            )}
                          </div>
                          <div>
                            <label className={labelClass}>Phone (optional)</label>
                            <input
                              type="tel"
                              name="phone"
                              value={serviceForm.phone}
                              onChange={handleServiceChange}
                              placeholder="(214) 555-0123"
                              className={fieldClass}
                            />
                          </div>
                        </div>
                        <div>
                          <label className={labelClass}>Website / Social Media (optional)</label>
                          <input
                            name="website"
                            value={serviceForm.website}
                            onChange={handleServiceChange}
                            placeholder="https://yourwebsite.com or Instagram / Facebook link"
                            className={fieldClass}
                          />
                        </div>
                      </div>
                    </div>

                    {serviceSubmitError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-[12.5px] rounded-xl px-4 py-3 mb-3">
                        {serviceSubmitError}
                      </div>
                    )}
                    {/* Submit */}
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={serviceLoading}
                        className="bg-[#51ba4f] hover:bg-[#43a041] text-white text-sm font-bold px-6 py-2.5 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {serviceLoading ? "Submitting…" : "Submit Service Listing"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setServiceFormOpen(false)}
                        className="text-sm font-semibold text-gray-500 hover:text-gray-700 px-4 py-2.5 rounded-md transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Split Feature Cards ── */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Card 1: POST A JOB */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="w-11 h-11 rounded-lg bg-[#082f6d]/10 flex items-center justify-center mb-4">
              <Briefcase className="w-5 h-5 text-[#082f6d]" />
            </div>
            <h3
              className="text-lg font-bold text-[#1f2937] mb-2"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Post a Job
            </h3>
            <p
              className="text-sm text-gray-600 mb-4"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Hiring for your restaurant, store, salon, or business? Post your opening and reach
              thousands of job seekers in the Dallas–Fort Worth Desi community.
            </p>
            <ul className="space-y-2 mb-5">
              {[
                "Reach local South Asian job seekers",
                "Full-time, part-time, contract & internship roles",
                "Free to post — listings go live after a quick review",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-[#1f2937]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#51ba4f] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <button
              onClick={() => {
                setFormOpen(true);
                setSubmitted(false);
              }}
              className="inline-flex items-center gap-1.5 bg-[#51ba4f] hover:bg-[#43a041] text-white text-[12px] font-bold px-3 py-1.5 rounded-md transition-colors duration-200"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <Briefcase className="w-3.5 h-3.5" />
              Post a Job
            </button>
            <p className="text-xs text-gray-400 mt-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Your listing appears once approved by the Dallas Desi Scoop team.
            </p>
          </motion.div>

          {/* Card 2: LOOKING FOR A JOB */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="w-11 h-11 rounded-lg bg-[#51ba4f]/10 flex items-center justify-center mb-4">
              <Search className="w-5 h-5 text-[#51ba4f]" />
            </div>
            <h3
              className="text-lg font-bold text-[#1f2937] mb-2"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Looking for a Job
            </h3>
            <p
              className="text-sm text-gray-600 mb-4"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Browse verified job openings from local Desi businesses across Dallas–Fort Worth.
              New listings are added as soon as employers post and they're approved.
            </p>
            <ul className="space-y-2 mb-5">
              {[
                "Openings from restaurants, retail, tech, beauty & more",
                "On-site, hybrid, and remote arrangements",
                "Apply directly using the employer's instructions",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-[#1f2937]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#51ba4f] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <button
              onClick={scrollToJobs}
              className="inline-flex items-center gap-1.5 bg-[#082f6d] hover:bg-[#061f4a] text-white text-[12px] font-bold px-3 py-1.5 rounded-md transition-colors duration-200"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <ChevronUp className="w-3.5 h-3.5" />
              Browse Job Openings
            </button>
            <p className="text-xs text-gray-400 mt-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {totalOpenings > 0
                ? `${totalOpenings} opening${totalOpenings === 1 ? "" : "s"} live right now.`
                : "No approved openings yet — check back soon."}
            </p>
          </motion.div>

          {/* Card 3: OFFER SERVICES */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="w-11 h-11 rounded-lg bg-[#f59e0b]/10 flex items-center justify-center mb-4">
              <Sparkles className="w-5 h-5 text-[#f59e0b]" />
            </div>
            <h3
              className="text-lg font-bold text-[#1f2937] mb-2"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Offer Services
            </h3>
            <p
              className="text-sm text-gray-600 mb-4"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Have a service, skill, or help to offer the community? From babysitting and
              tutoring to home cooking, photography, cleaning, and tech help.
            </p>
            <ul className="space-y-2 mb-5">
              {[
                "Babysitting, tutoring, home cooking, cleaning & more",
                "Freelancers, students, and hobbyists welcome",
                "Free to list — your service goes live after a quick review",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-[#1f2937]"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#51ba4f] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <button
              onClick={() => {
                setServiceFormOpen(true);
                setServiceSubmitted(false);
              }}
              className="inline-flex items-center gap-1.5 bg-[#f59e0b] hover:bg-[#d97706] text-white text-[12px] font-bold px-3 py-1.5 rounded-md transition-colors duration-200"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <Sparkles className="w-3.5 h-3.5" />
              List Your Service
            </button>
            <p className="text-xs text-gray-400 mt-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Your service listing appears once approved by the Dallas Desi Scoop team.
            </p>
          </motion.div>
        </div>

        {/* ── LOOKING FOR A JOB: approved listings ── */}
        <div id="job-listings" className="mt-16 scroll-mt-24">
          <div className="flex items-end justify-between flex-wrap gap-2 mb-6">
            <div>
              <h2
                className="text-lg md:text-xl font-bold text-[#1f2937]"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                Looking for a Job? Browse Openings
              </h2>
              <p
                className="text-sm text-gray-600 mt-1"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Approved job listings from local Dallas–Fort Worth Desi businesses.
              </p>
            </div>
            <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#51ba4f]/10 text-[#51ba4f] border border-[#51ba4f]/20">
              {totalOpenings} opening{totalOpenings === 1 ? "" : "s"}
            </span>
          </div>

          {/* ── Featured job listings (published by Dallas Desi Scoop) ── */}
          {!jobsLoading && FEATURED_JOBS.length > 0 && (
            <div className="grid grid-cols-1 gap-6 mb-8">
              {FEATURED_JOBS.map((job, i) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.3) }}
                  className="bg-white border-2 border-[#51ba4f]/30 rounded-xl p-6 sm:p-8 shadow-sm"
                >
                  <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                    <div>
                      <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#f59e0b]/10 text-[#f59e0b] border border-[#f59e0b]/20 mb-2">
                        FEATURED
                      </span>
                      <h3
                        className="text-lg md:text-xl font-bold text-[#1f2937] leading-snug"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {job.jobTitle}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#082f6d]/5 text-[#082f6d] border border-[#082f6d]/10">
                        {job.jobType}
                      </span>
                      <span className="text-[10px] text-gray-400">Posted {job.postedDate}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mb-4">
                    <div className="flex items-center gap-1.5 text-sm text-gray-700" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span>{job.companyName}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span>
                        {job.location} · {job.workArrangement}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="font-bold text-[#1f2937]">{job.jobCategory}</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-bold text-[#1f2937] mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        About the Role
                      </h4>
                      <p className="text-sm text-gray-600" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                        {job.about}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[#1f2937] mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        Who Can Apply?
                      </h4>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                        {job.whoCanApply.map((item, j) => (
                          <li
                            key={j}
                            className="flex items-start gap-2 text-sm text-gray-600"
                            style={{ fontFamily: "'Montserrat', sans-serif" }}
                          >
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#51ba4f] flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-[#082f6d]/5 border border-[#082f6d]/10 rounded-lg px-4 py-3">
                      <h4 className="text-sm font-bold text-[#082f6d] mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        Sponsorship Opportunity
                      </h4>
                      <p className="text-sm text-gray-600" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                        {job.sponsorship}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-[#1f2937]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      <span className="font-semibold">How to apply: </span>
                      {job.howToApply}
                    </p>
                    <a
                      href={job.companyWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#082f6d] hover:underline"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      <Globe className="w-3.5 h-3.5" />
                      gehilaw.com
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {jobsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : totalOpenings === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
              <Briefcase className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                No job openings right now. New listings appear here as soon as they're approved —
                check back soon!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {approvedJobs.map((job: any, i: number) => {
                const website = field(job, "companyWebsite");
                return (
                  <motion.div
                    key={job.id || job._id || i}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.3) }}
                    className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h3
                        className="text-base font-bold text-[#1f2937] leading-snug"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {field(job, "jobTitle") || "Job Opening"}
                      </h3>
                      {field(job, "jobType") && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#082f6d]/5 text-[#082f6d] border border-[#082f6d]/10 shrink-0">
                          {field(job, "jobType")}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 text-sm text-gray-700 mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="truncate">{field(job, "companyName")}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="truncate">
                        {field(job, "location")}
                        {field(job, "workArrangement") ? ` · ${field(job, "workArrangement")}` : ""}
                      </span>
                    </div>
                    {field(job, "jobCategory") && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                        <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="truncate">{field(job, "jobCategory")}</span>
                      </div>
                    )}

                    {field(job, "jobDescription") && (
                      <p
                        className="text-sm text-gray-600 mb-4 line-clamp-4"
                        style={{ fontFamily: "'Montserrat', sans-serif" }}
                      >
                        {field(job, "jobDescription")}
                      </p>
                    )}

                    <div className="mt-auto pt-4 border-t border-gray-100 space-y-2">
                      <p className="text-xs text-[#1f2937]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                        <span className="font-semibold">How to apply: </span>
                        {field(job, "howToApply")}
                      </p>
                      {website && (
                        <a
                          href={website.startsWith("http") ? website : `https://${website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#082f6d] hover:underline"
                          style={{ fontFamily: "'Montserrat', sans-serif" }}
                        >
                          <Globe className="w-3.5 h-3.5" />
                          Company Website
                        </a>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Closing blurb ── */}
        <div className="mt-12 pt-8 border-t border-gray-100">
          <p
            className="text-sm md:text-base text-gray-600 max-w-2xl"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Find and connect with individuals and businesses offering local services in
            Dallas–Fort Worth, including babysitting, tutoring, home cooking, photography,
            beauty services, event services, tech help, transportation, and more. Have a skill
            or service to offer? List your service and let people in the local community
            discover and contact you.
          </p>
          <p
            className="text-sm md:text-base font-bold text-[#082f6d] mt-4"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Find jobs. Hire talent. Discover local services. Connect across DFW.
          </p>
        </div>
      </div>
    </div>
  );
}
